import { useState } from "react";
import useSWR from "swr";

import { api, MyUsage, UsageRequestPage } from "../api";
import { Icon, PageHeader } from "../ui";

const fetcher = <T,>(path: string) => api.get<T>(path);

const DAYS = 30;
const PAGE_SIZE = 25;

const STATUS_TEXT: Record<number, string> = {
  200: "OK",
  400: "Bad request",
  401: "Unauthorized",
  403: "Forbidden",
  404: "Not found",
  422: "Invalid",
  429: "Rate limited",
  500: "Server error",
};

/** Every UTC date in the window, oldest first, zero-filled.
 *
 * The server window is a rolling `days` × 24h grouped by UTC date, so it
 * touches days + 1 dates (the oldest one partial); covering all of them keeps
 * the bars summing to the total.
 */
export function fillDays(byDay: { day: string; count: number }[], days: number, now = new Date()) {
  const counts = new Map(byDay.map((d) => [d.day, d.count]));
  return Array.from({ length: days + 1 }, (_, i) => {
    const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - days + i))
      .toISOString()
      .slice(0, 10);
    return { day, count: counts.get(day) ?? 0 };
  });
}

// Day strings are UTC dates; format them in UTC so they don't shift a day.
const fmtDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Query strings read better decoded (`manager=1512 SG`, not `manager=1512+SG`).
export function fmtQuery(query: string) {
  try {
    return decodeURIComponent(query.replace(/\+/g, " "));
  } catch {
    return query;
  }
}

function statusClass(status: number) {
  if (status < 400) return "bg-pos-bg text-pos";
  if (status < 500) return "bg-warning-bg text-warning";
  return "bg-neg-bg text-neg";
}

export function Usage() {
  const { data, error } = useSWR<MyUsage>(`/usage/summary?days=${DAYS}`, fetcher);

  const routed = data?.by_route.reduce((n, r) => n + r.count, 0) ?? 0;
  const avgMs = data && routed ? Math.round(data.by_route.reduce((n, r) => n + r.count * r.avg_ms, 0) / routed) : null;

  return (
    <section className="flex flex-col gap-8">
      <PageHeader
        title="Usage"
        description={`Data requests made with your account over the last ${DAYS} days. Sign-in and account calls aren't counted.`}
      />

      {error && <p className="text-sm text-neg">Could not load usage.</p>}
      {!data && !error && <p className="text-sm text-muted">Loading…</p>}
      {data && (
        <>
          <div className="grid grid-cols-3 max-sm:grid-cols-1 gap-4">
            <Stat label="Requests" value={data.total.toLocaleString()} />
            <Stat
              label="Errors"
              value={data.error_count.toLocaleString()}
              hint={data.total ? `${((100 * data.error_count) / data.total).toFixed(1)}% of requests` : undefined}
            />
            <Stat label="Avg response time" value={avgMs === null ? "—" : `${avgMs} ms`} />
          </div>
          <DailyBars days={fillDays(data.by_day, DAYS)} />
        </>
      )}

      <RequestLog />
    </section>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-1.5 p-5 rounded-lg bg-card border border-border">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-2xl font-semibold text-main tabular-nums">{value}</span>
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </div>
  );
}

function DailyBars({ days }: { days: { day: string; count: number }[] }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="p-5 rounded-lg bg-card border border-border">
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <h2 className="text-[15px] font-semibold text-main">Requests per day</h2>
        <span className="text-xs text-muted">UTC · peak {max.toLocaleString()}</span>
      </div>
      <ul className="flex items-end gap-0.5 h-40 border-b border-border" aria-label="Requests per day">
        {days.map((d, i) => {
          const pct = (100 * d.count) / max;
          const align = i < 3 ? "left-0" : i > days.length - 4 ? "right-0" : "left-1/2 -translate-x-1/2";
          return (
            // The whole column is the hover target, not just the (possibly tiny) bar.
            <li key={d.day} className="group relative flex-1 h-full flex items-end" aria-label={`${fmtDay(d.day)}: ${d.count} requests`}>
              <div
                className="w-full rounded-t bg-chart group-hover:opacity-75"
                style={{ height: `${pct}%`, minHeight: d.count ? 2 : 0 }}
              />
              <div
                className={`pointer-events-none absolute z-10 mb-2 hidden group-hover:block whitespace-nowrap rounded-md px-2 py-1 text-xs bg-main text-surface ${align}`}
                style={{ bottom: `${pct}%` }}
              >
                {fmtDay(d.day)} · {d.count.toLocaleString()} requests
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-between mt-2 text-xs text-muted">
        <span>{fmtDay(days[0].day)}</span>
        <span>{fmtDay(days[days.length - 1].day)}</span>
      </div>
    </div>
  );
}

/** Requests newest first. Admins point it at /admin/usage/requests, with a User column when it spans everyone. */
export function RequestLog({ source = "/usage/requests", showUser = false }: { source?: string; showUser?: boolean }) {
  // Keyset paging: each entry is the before_id that opened a later page.
  const [cursors, setCursors] = useState<number[]>([]);
  const before = cursors[cursors.length - 1];
  const { data, error } = useSWR<UsageRequestPage>(
    `${source}${source.includes("?") ? "&" : "?"}limit=${PAGE_SIZE}${before ? `&before_id=${before}` : ""}`,
    fetcher,
    { keepPreviousData: true },
  );
  const items = data?.items ?? [];
  const start = cursors.length * PAGE_SIZE + 1;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-main">Recent requests</h2>
      {error && <p className="text-sm text-neg">Could not load requests.</p>}
      {!data && !error && <p className="text-sm text-muted">Loading…</p>}
      {data && items.length === 0 && cursors.length === 0 && (
        source !== "/usage/requests" ? (
          <p className="text-sm text-muted">No requests in the last 90 days.</p>
        ) : (
          <p className="text-sm text-muted">
            No requests yet. Data calls made with your tokens show up here. <a href="/">Make your first request</a>
          </p>
        )
      )}
      {items.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Request</th>
                {showUser && <th>User</th>}
                <th className="max-md:hidden">Token</th>
                <th className="text-right">Response time</th>
                <th className="max-sm:hidden">Time</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">
                    <span className={`text-xs font-medium px-2 py-1 rounded-md ${statusClass(r.status)}`}>
                      {r.status} {STATUS_TEXT[r.status] ?? ""}
                    </span>
                  </td>
                  <td className="font-mono text-[13px] break-all min-w-64">
                    <span className="text-muted">{r.method}</span> <span className="text-main">{r.path.replace(/^\/api\/v1/, "")}</span>
                    {r.query && <span className="text-muted">?{fmtQuery(r.query)}</span>}
                  </td>
                  {showUser && <td className="whitespace-nowrap">{r.email ?? <span className="text-muted">unauthenticated</span>}</td>}
                  <td className="max-md:hidden whitespace-nowrap">
                    {r.token_name ?? <span title="Browser session, or a token that has since been deleted">—</span>}
                  </td>
                  <td className="text-right tabular-nums whitespace-nowrap">{r.duration_ms} ms</td>
                  <td className="max-sm:hidden whitespace-nowrap">{fmtTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-border text-sm text-muted">
            <span>
              Showing {start}–{start + items.length - 1}
              {data?.has_more ? " of more" : ""}
            </span>
            <div className="flex gap-1.5">
              <button
                className="btn-sm btn-default"
                disabled={cursors.length === 0}
                onClick={() => setCursors((c) => c.slice(0, -1))}
                aria-label="Newer requests"
              >
                <Icon name="chevronLeft" className="w-4 h-4" />
              </button>
              <button
                className="btn-sm btn-default"
                disabled={!data?.has_more}
                onClick={() => setCursors((c) => [...c, items[items.length - 1].id])}
                aria-label="Older requests"
              >
                <Icon name="chevronRight" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
